import { CheckboxGroupFormComponent } from "components/Form/components/CheckboxGroup/CheckboxGroupFormComponent";
import { Form } from "components/Form/components/Form/Form";
import { TextFormComponent } from "components/Form/components/Text/TextFormComponent";
import { IconCollection } from "components/Icon/IconCollection";
import PageContent from "components/Layout/PageContent";
import { Popover, popoverPosition } from "components/Popover/Popover";
import RecordLabel from "components/RecordLabel";
import RecordTable from "components/Table/RecordTable";
import { CheckboxTriggerButton } from "containers/AdvancedSearch/components/CheckboxTriggerButton";
import {
    fetchIdentityList,
    fetchInitialIdentityListCount,
} from "containers/Auth/screens/IdentityManagement/IdentityRole.service";
import {
    debouncedSubmit,
    onCheckboxValueChange,
    submitFilterForm,
    useQueryParams,
} from "containers/ExecutionFilter/utils";
import { ClearSearchButton } from "containers/Filter/components/ClearSearchButton";
import clearSearchStyles from "containers/Filter/components/SearchButton.module.scss";
import { AddRoleIdentityPopover } from "containers/Resources/screens/ResourcesSingle/components/RoleIdentityList/RoleIdentityForm";
import { useCallback, useEffect, useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { useDispatch, useSelector } from "react-redux";
import { getRelativeUrl } from "utils/service";
import { getFilterString } from "utils/utils";
import { RoleIdentityToolbar } from "../ResourceSingleToolbar";
import "../SubrecordTable/SubrecordTable.scss";
import { getInitialCountIri, initialFilter, roleIdentityListKey, typeList } from "./constants";
import { makeQueryString, useIdentityRoleFilter } from "./utils";

interface RoleIdentityListProps {
    updateIdentityQuery: (changedQuery: any) => void;
    identityQuery: {
        limit: number;
        offset: number;
    };
    isActionButtonDisabled: boolean;
    roleId: string;
}

export const RoleIdentityList = ({
    updateIdentityQuery,
    identityQuery,
    isActionButtonDisabled,
    roleId,
}: RoleIdentityListProps) => {
    const dispatch = useDispatch();
    const { queryParams } = useQueryParams();
    const recordId = queryParams.id ?? queryParams.name;

    const { filteredValues, setQueryString, setInitialFilterHandler, getFilters, effectiveQueryParams } =
        useIdentityRoleFilter(queryParams, roleId);

    const [isLoading, setLoading] = useState(false);

    const fetchIdentityRoleList = useCallback(() => {
        setLoading(true);
        fetchIdentityList(dispatch, roleId, {
            filter: makeQueryString(effectiveQueryParams.filter, roleId),
            ...identityQuery,
        }).finally(() => setLoading(false));
    }, [dispatch, effectiveQueryParams.filter, identityQuery, roleId]);

    useEffect(() => {
        if (roleId && effectiveQueryParams?.filter) {
            fetchIdentityRoleList();
        }
    }, [fetchIdentityRoleList, effectiveQueryParams?.filter, roleId]);

    const identityListData = useSelector(
        (state: any) => state?.identityRole?.[roleId]?.identity_role_list ?? {}
    );
    const initialIdentityListCount =
        useSelector(
            (state: any) =>
                state?.identityRole?.[getInitialCountIri(roleId)]?.identity_role_list
                    ?.identity_role_count
        ) ?? 0;
    const identityList = identityListData.identity_role_list;

    const totalIdentityCount = identityListData.identity_role_count ?? 0;

    const { identity_type: type } = filteredValues;

    useEffect(() => {
        if (!effectiveQueryParams?.filter) {
            setInitialFilterHandler("replace");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId]);

    useEffect(() => {
        if (totalIdentityCount > 0 && !identityList?.length && identityQuery.offset) {
            updateIdentityQuery({ offset: 0 });
        }
    }, [identityList?.length, identityQuery.offset, totalIdentityCount, updateIdentityQuery]);

    useEffect(() => {
        if (totalIdentityCount === 0) {
            // if totalIdentityCount === 0 need to fetch initialIdentityCount
            // to display filters with an empty table or the text "This role is not used by any identity" if no identities initially
            fetchInitialIdentityListCount(dispatch, roleId, {
                filter: makeQueryString(getFilterString(getFilters(initialFilter)), roleId),
            });
        }
    }, [dispatch, getFilters, roleId, totalIdentityCount]);

    return (
        <PageContent key="roleIdentityList" className="subrecordList">
            <div className="subrecordHeader">
                <h3>Used by</h3>
                {roleId && (
                    <AddRoleIdentityPopover
                        isActionButtonDisabled={isActionButtonDisabled}
                        roleId={roleId}
                        fetchResourceData={fetchIdentityRoleList}
                        currentIdentityList={identityList}
                    />
                )}
            </div>
            {initialIdentityListCount > 0 || identityList?.length > 0 ? (
                <>
                    <FinalForm
                        onSubmit={(formData) =>
                            submitFilterForm({
                                prevValues: filteredValues,
                                newValues: formData,
                                setQueryString,
                                textFields: ["name"],
                            })
                        }
                        initialValues={filteredValues}
                    >
                        {({ handleSubmit, values, form, pristine }) => {
                            const submitForm = () => {
                                if (!pristine) {
                                    handleSubmit();
                                }
                            };
                            return (
                                <Form onSubmit={handleSubmit}>
                                    <div className="execution-search-filters advanced-search-filters">
                                        <Form.Column>
                                            <Field
                                                name="name"
                                                id="name"
                                                fieldAttr={{
                                                    placeholder: "Name contains...",
                                                }}
                                                component={TextFormComponent}
                                            />
                                            <OnChange name="name">
                                                {() => debouncedSubmit(handleSubmit)}
                                            </OnChange>
                                        </Form.Column>
                                        <Form.Column>
                                            <Popover
                                                trigger={
                                                    <CheckboxTriggerButton
                                                        label="Type"
                                                        currentFilter={type}
                                                        totalItemCount={
                                                            initialFilter.identity_type.length
                                                        }
                                                    />
                                                }
                                                closeOnInsideClick={false}
                                                onBlur={submitForm}
                                                {...popoverPosition}
                                            >
                                                <Field
                                                    name="identity_type"
                                                    id="identity_type"
                                                    component={CheckboxGroupFormComponent}
                                                    fieldProps={{
                                                        options: typeList.map((type) => ({
                                                            value: type.resourceName,
                                                            label: type.label,
                                                        })),
                                                        currentValue: values.identity_type,
                                                        onChange: (newValue) => (e) => {
                                                            onCheckboxValueChange({
                                                                form,
                                                                identifier: "identity_type",
                                                                currentValue: values.identity_type,
                                                                newValue,
                                                            })(e);
                                                        },
                                                    }}
                                                />
                                            </Popover>
                                        </Form.Column>
                                        <ClearSearchButton
                                            className={clearSearchStyles.clearSearchButtonSingle}
                                            onClear={() => setInitialFilterHandler("push")}
                                        />
                                    </div>
                                </Form>
                            );
                        }}
                    </FinalForm>
                    <RecordTable
                        data={identityList}
                        totalItemCount={totalIdentityCount}
                        options={{
                            toolbar: "custom",
                            customLeftToolbar: RoleIdentityToolbar as any,
                            density: "high",
                            currentOffset: identityQuery.offset,
                        }}
                        updateQuery={updateIdentityQuery}
                        initialQuery={{
                            limit: identityQuery.limit,
                            offset: identityQuery.offset,
                            order: "",
                        }}
                        tableSettingsKey={roleIdentityListKey}
                        refresh={fetchIdentityRoleList}
                        isLoading={isLoading}
                        columns={[
                            {
                                label: "Type",
                                key: "user_type",
                                shown: true,
                                sortable: false,
                                dto: ({ identity_id_identity, identity_id }) => {
                                    return (
                                        <RecordLabel
                                            key={identity_id}
                                            link={
                                                identity_id && identity_id_identity?.identity_type
                                                    ? getRelativeUrl(
                                                          identity_id_identity.identity_type,
                                                          identity_id
                                                      )
                                                    : null
                                            }
                                            name={identity_id_identity?.identity_type ?? "unknown"}
                                        />
                                    );
                                },
                            },
                            {
                                label: "Name",
                                key: "name",
                                shown: true,
                                sortable: false,
                                dto: ({ identity_id_identity, identity_id }) => {
                                    return (
                                        <RecordLabel
                                            key={identity_id}
                                            link={
                                                identity_id && identity_id_identity?.identity_type
                                                    ? getRelativeUrl(
                                                          identity_id_identity.identity_type,
                                                          identity_id
                                                      )
                                                    : null
                                            }
                                            name={
                                                identity_id_identity?.name ??
                                                `unknown with id "${identity_id}"`
                                            }
                                        />
                                    );
                                },
                            },
                            {
                                label: "Propagate",
                                key: "propagate",
                                shown: true,
                                sortable: false,
                                dto: ({ identity_id_identity, identity_id, propagate }) => (
                                    <RecordLabel
                                        key={identity_id}
                                        link={
                                            identity_id && identity_id_identity?.identity_type
                                                ? getRelativeUrl(
                                                      identity_id_identity.identity_type,
                                                      identity_id
                                                  )
                                                : null
                                        }
                                        icon={propagate ? IconCollection.check : undefined}
                                    />
                                ),
                            },
                        ]}
                        recordTypes={null}
                    />
                </>
            ) : (
                <div className="subrecord-empty-list">This role is not used by any identity.</div>
            )}
        </PageContent>
    );
};
